import { useCallback, useEffect, useRef, useState } from 'react';

export function useDeferredLoading(minDuration = 300) {
  const [showSpinner, setShowSpinner] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shownAtRef = useRef<number | null>(null);

  const start = useCallback(() => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (shownAtRef.current !== null) return;

    shownAtRef.current = Date.now();
    setShowSpinner(true);
  }, []);

  const stop = useCallback(() => {
    if (shownAtRef.current === null) return;

    const elapsed = Date.now() - shownAtRef.current;
    const wait = Math.max(0, minDuration - elapsed);

    hideTimerRef.current = setTimeout(() => {
      setShowSpinner(false);
      shownAtRef.current = null;
      hideTimerRef.current = null;
    }, wait);
  }, [minDuration]);

  const reset = useCallback(() => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setShowSpinner(false);
    shownAtRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current !== null) clearTimeout(hideTimerRef.current);
    };
  }, []);

  return { showSpinner, start, stop, reset };
}
